import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { ArticleCard } from "../components/ArticleCard";
import { TrendingSidebar } from "../components/TrendingSidebar";
import { CATEGORY_LABELS } from "../lib/constants";

// The signature of this template: every department gets its own full-bleed
// band, alternating between the paper ground and the tinted stock, ruled off
// from its neighbours by a single emerald-tinted hairline. The band supplies
// the full-width colour; the container inside keeps the measure honest.
function Band({ tinted = false, children }) {
    return (
        <div className={`border-b border-line ${tinted ? "bg-surface-raised" : "bg-canvas"}`}>
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">{children}</div>
        </div>
    );
}

// Small-caps emerald marker over a serif title, with a thick emerald rule
// carried under the title only — never the full measure.
function SectionHeader({ title, to, marker }) {
    return (
        <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3 mb-8">
            <div>
                {marker && <span className="eyebrow block text-accent mb-2 tabular-nums">{marker}</span>}
                <h2 className="font-display text-[1.6rem] sm:text-[2rem] font-bold leading-none inline-block border-b-[3px] border-accent pb-2.5">
                    {title}
                </h2>
            </div>
            {to && (
                <Link
                    to={to}
                    className="eyebrow text-dim hover:text-accent link-underline transition-colors duration-150 shrink-0 pb-1"
                >
                    View all
                </Link>
            )}
        </div>
    );
}

function HomeSkeleton() {
    return (
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <p className="flex items-center gap-2 eyebrow text-accent mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse-dot" />
                Loading the newsroom…
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
                <div className="lg:col-span-8">
                    <div className="shimmer h-10 w-3/4" />
                    <div className="shimmer h-5 w-1/2 mt-4" />
                    <div className="shimmer aspect-[16/10] mt-6" />
                </div>
                <div className="lg:col-span-4 space-y-3">
                    {[0, 1, 2, 3, 4].map((i) => (
                        <div key={i} className="shimmer h-16" />
                    ))}
                </div>
            </div>
        </div>
    );
}

export default function Home() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/home").then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
    }, []);

    if (loading) {
        return <HomeSkeleton />;
    }

    if (!data || !data.featured) {
        return (
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="home-empty">
                <div className="panel max-w-lg mx-auto p-12">
                    <span className="eyebrow text-accent">Standby</span>
                    <h2 className="font-display text-2xl font-bold mt-3 inline-block border-b-[3px] border-accent pb-2">
                        Fetching the latest stories
                    </h2>
                    <p className="mt-4 text-sm text-dim leading-relaxed">
                        Articles are being pulled from The Guardian. Please check back in a moment.
                    </p>
                </div>
            </div>
        );
    }

    const { featured, latest, trending, sections } = data;

    return (
        <div data-testid="home-page">
            {/* Lead band — the day's lead story alongside the trending well */}
            <Band>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
                    <div className="lg:col-span-8">
                        <ArticleCard article={featured} variant="hero" />
                    </div>
                    <div className="lg:col-span-4">
                        <TrendingSidebar items={trending} />
                    </div>
                </div>
            </Band>

            {/* Latest — a ruled three-column list on the tinted stock */}
            <Band tinted>
                <SectionHeader title="Latest News" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10">
                    {latest.slice(0, 6).map((a) => (
                        <ArticleCard key={a.id} article={a} variant="compact" />
                    ))}
                </div>
            </Band>

            {/* Departments — one band each, alternating ground */}
            {Object.entries(sections).map(([cat, arts], i) => (
                <Band key={cat} tinted={i % 2 === 1}>
                    <section data-testid={`home-section-${cat}`}>
                        <SectionHeader
                            title={CATEGORY_LABELS[cat] || cat}
                            to={`/category/${cat}`}
                            marker={String(i + 1).padStart(2, "0")}
                        />
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {arts.map((a) => (
                                <ArticleCard key={a.id} article={a} />
                            ))}
                        </div>
                    </section>
                </Band>
            ))}
        </div>
    );
}
